import React from 'react';
import cn from 'classnames';
import get from 'lodash.get';
import { selectorEncode } from '../../utils/navigation';
import {
    displayPercentOrNaTruthyZero,
    displayShortDateOrNA,
    NA_DEFAULT
} from '../../utils/Format/Format';
import { quarterEndBeforeInception } from '../../utils/fund-info';

const processValue = (valueConfig) => {
    if (!valueConfig) return NA_DEFAULT;

    const { value, disclaimPerformance } = valueConfig;
    const textValue = displayPercentOrNaTruthyZero(value);

    if (disclaimPerformance && value >= 40) {
        return (
            <>
                {textValue}
                <sup> †</sup>
            </>
        );
    }

    return textValue;
};

export const etfAverageAnnualTableCardUtil = (period) => (gqlResult) => {
    if (!gqlResult) return null;

    const {
        inceptionDate,
        grossExpenseRatio,
        netExpenseRatio,
        averageAnnualTotalReturns,
        benchmarks
    } = gqlResult.exchangeTradedFund;

    const TOTAL_RETURNS_DEFAULT = {
        monthly: {
            fundPerformance: [],
            benchmarkPerformance: []
        },
        quarterly: {
            fundPerformance: [],
            benchmarkPerformance: []
        }
    };

    const returnsData = averageAnnualTotalReturns || TOTAL_RETURNS_DEFAULT;

    const getAATRDataByPeriod = (path) => get(returnsData[period], path);

    const dateByPeriod = getAATRDataByPeriod('asOfDate');

    const benchmarkPerformanceByPeriod =
        getAATRDataByPeriod('benchmarkPerformance').length > 0
            ? getAATRDataByPeriod('benchmarkPerformance')
            : Array(14).fill('N/A');

    let fundPerformanceByPeriod =
        getAATRDataByPeriod('fundPerformance').length > 0
            ? getAATRDataByPeriod('fundPerformance')
            : Array(14).fill('N/A');

    const quarterlyEndDate =
        returnsData && returnsData.quarterly && returnsData.quarterly.asOfDate;

    const primaryBenchmark =
        benchmarks &&
        benchmarks.find((benchmark) => benchmark.benchmarkType === 'Primary');

    const primaryBenchMarkPerformance = () => {
        let filteredArr = [];
        if (primaryBenchmark) {
            filteredArr =
                benchmarkPerformanceByPeriod &&
                benchmarkPerformanceByPeriod.filter(
                    (performance) =>
                        performance.benchmarkCode ===
                            primaryBenchmark.benchmarkCode &&
                        performance.performanceType === 'Net'
                );
        }
        if (filteredArr.length === 0) {
            return benchmarkPerformanceByPeriod || [];
        }
        return filteredArr;
    };
    // benchmark since inception will need to be updated after a year to pull annualized value - TBD US231680
    const [
        {
            navOneMonthReturn: benchmarkOneMonthReturn,
            navThreeMonthReturn: benchmarkThreeMonthReturn,
            navOneYearReturn: benchmarkOneYearReturn,
            navThreeYearAnnualizedReturn: benchmarkThreeYearAnnualReturn,
            navFiveYearAnnualizedReturn: benchmarkFiveYearAnnualReturn,
            navTenYearAnnualizedReturn: benchmarkTenYearAnnualReturn,
            navSinceInceptionReturn: benchmarkSinceInceptionReturn,
            navSinceInceptionAnnualizedReturn: benchmarkSinceInceptionAnnualizedReturn,
            valueAddedOneMonthReturn,
            valueAddedThreeMonthReturn,
            valueAddedOneYearReturn,
            valueAddedThreeYearAnnualizedReturn: valueAddedThreeYearReturn,
            valueAddedFiveYearAnnualizedReturn: valueAddedFiveYearReturn,
            valueAddedTenYearAnnualizedReturn: valueAddedTenYearReturn,
            valueAddedSinceInceptionReturn,
            valueAddedSinceInceptionAnnualizedReturn
        }
    ] = primaryBenchMarkPerformance();

    // DE44005 - Filter out performance of type "Gross"
    fundPerformanceByPeriod = fundPerformanceByPeriod.filter(
        ({ performanceType }) => {
            if (performanceType && performanceType === 'Gross') {
                return false;
            }

            return true;
        }
    );

    const [
        {
            navOneMonthReturn,
            navThreeMonthReturn,
            navOneYearReturn,
            navThreeYearAnnualizedReturn: navThreeYearReturn,
            navFiveYearAnnualizedReturn: navFiveYearReturn,
            navTenYearAnnualizedReturn: navTenYearReturn,
            navSinceInceptionReturn,
            navSinceInceptionAnnualizedReturn,
            marketPriceOneMonthReturn,
            marketPriceThreeMonthReturn,
            marketPriceOneYearReturn,
            marketPriceThreeYearAnnualizedReturn: marketPriceThreeYearReturn,
            marketPriceFiveYearAnnualizedReturn: marketPriceFiveYearReturn,
            marketPriceTenYearAnnualizedReturn: marketPriceTenYearReturn,
            marketPriceSinceInceptionReturn,
            marketPriceSinceInceptionAnnualizedReturn
        }
    ] =
        fundPerformanceByPeriod || [];

    const { daily } = averageAnnualTotalReturns || {};
    const getPrimaryBenchmarkDailyData = (dailyBenchmarks = []) => {
        const primaryDailyBenchmark = dailyBenchmarks.filter(
            (benchmark) =>
                benchmark.benchmarkCode === primaryBenchmark.benchmarkCode
        );
        if (!(primaryDailyBenchmark && primaryDailyBenchmark.length)) {
            return [{}];
        }
        return primaryDailyBenchmark;
    };

    const {
        asOfDate: dailyDate,
        fundPerformance: dailyFundPerformance = [],
        benchmarkPerformance: dailyBenchmarkPerformance
    } =
        daily || {};

    const primaryDailyBenchmarkPerformance = getPrimaryBenchmarkDailyData(
        dailyBenchmarkPerformance
    );

    const [
        { navDailyYtdReturn, marketPriceDailyYtdReturn }
    ] = dailyFundPerformance.length ? dailyFundPerformance : [{}];

    const [
        { navDailyYtdReturn: benchmarkDailyYtdReturn, valueAddedDailyYtdReturn }
    ] = primaryDailyBenchmarkPerformance;

    const displayAnnualizedIfExists = (annualized, regular) =>
        annualized || regular;

    const headers = [
        '',
        '1 Month',
        '3 Month',
        '1 Year',
        '3 Year',
        '5 Year',
        '10 Year',
        <span key="daily-ytd-label">
            Daily YTD<br />
            (as of {displayShortDateOrNA(dailyDate)})
        </span>,
        'Since Inception'
    ];

    const returnData = [
        {
            label: 'Market Price',
            values: [
                {
                    value: marketPriceOneMonthReturn,
                    disclaimPerformance: true
                },
                {
                    value: marketPriceThreeMonthReturn,
                    disclaimPerformance: true
                },
                {
                    value: marketPriceOneYearReturn,
                    disclaimPerformance: true
                },
                {
                    value: marketPriceThreeYearReturn,
                    disclaimPerformance: true
                },
                marketPriceFiveYearReturn,
                marketPriceTenYearReturn,
                {
                    value: marketPriceDailyYtdReturn,
                    disclaimPerformance: true
                },
                !quarterEndBeforeInception(inceptionDate) &&
                    displayAnnualizedIfExists(
                        marketPriceSinceInceptionAnnualizedReturn,
                        marketPriceSinceInceptionReturn
                    )
            ],
            tipContent:
                'Market Price return represents changes to the mid-point price and accounts for distributions from the fund. The midpoint is the average of bid/ask prices at market close, typically 4:00 PM (when NAV is normally determined for most funds).'
        },
        {
            label: 'NAV',
            values: [
                {
                    value: navOneMonthReturn,
                    disclaimPerformance: true
                },
                {
                    value: navThreeMonthReturn,
                    disclaimPerformance: true
                },
                {
                    value: navOneYearReturn,
                    disclaimPerformance: true
                },
                {
                    value: navThreeYearReturn,
                    disclaimPerformance: true
                },
                navFiveYearReturn,
                navTenYearReturn,
                {
                    value: navDailyYtdReturn,
                    disclaimPerformance: true
                },
                !quarterEndBeforeInception(inceptionDate) &&
                    displayAnnualizedIfExists(
                        navSinceInceptionAnnualizedReturn,
                        navSinceInceptionReturn
                    )
            ],
            tipContent:
                'Return represents changes to the NAV and accounts for distributions from the fund.'
        },
        {
            label: 'Benchmark',
            values: [
                {
                    value: benchmarkOneMonthReturn,
                    disclaimPerformance: true
                },
                {
                    value: benchmarkThreeMonthReturn,
                    disclaimPerformance: true
                },
                {
                    value: benchmarkOneYearReturn,
                    disclaimPerformance: true
                },
                {
                    value: benchmarkThreeYearAnnualReturn,
                    disclaimPerformance: true
                },
                benchmarkFiveYearAnnualReturn,
                benchmarkTenYearAnnualReturn,
                {
                    value: benchmarkDailyYtdReturn,
                    disclaimPerformance: true
                },
                /**
                 * DE52474: as per business, the Since Inception field logic
                 * for the Benchmark row should match the NAV and Market Price
                 * Since Inception fields
                 */
                !quarterEndBeforeInception(inceptionDate) &&
                    displayAnnualizedIfExists(
                        benchmarkSinceInceptionAnnualizedReturn,
                        benchmarkSinceInceptionReturn
                    )
            ],
            tipContent:
                'A benchmark is a unmanaged group of securities, usually an Index, that is used as a standard to measure a Fund’s performance.'
        },
        {
            label: 'Value Added',
            values: [
                valueAddedOneMonthReturn,
                valueAddedThreeMonthReturn,
                valueAddedOneYearReturn,
                valueAddedThreeYearReturn,
                valueAddedFiveYearReturn,
                valueAddedTenYearReturn,
                valueAddedDailyYtdReturn,
                !quarterEndBeforeInception(inceptionDate) &&
                    displayAnnualizedIfExists(
                        valueAddedSinceInceptionAnnualizedReturn,
                        valueAddedSinceInceptionReturn
                    )
            ],
            tipContent:
                'Value added is the excess return of an investment relative to the return of the benchmark.	Value Added is the performance difference between the NAV and benchmark returns.'
        }
    ].map((row) => ({
        ...row,
        // why the hell is this the interface
        // for initializing tables
        values: row.values.map((value) => {
            const cellContent =
                typeof value === 'number'
                    ? displayPercentOrNaTruthyZero(value)
                    : processValue(value);
            const numericalValue =
                typeof value === 'number' ? value : value && value.value;

            return {
                thing: (
                    <span
                        className={cn(selectorEncode(row.label), 'value', {
                            positive: numericalValue > 0,
                            negative: numericalValue < 0
                        })}
                    >
                        {cellContent}
                    </span>
                )
            };
        })
    }));

    const listOfPerformanceChecks = [
        marketPriceOneMonthReturn,
        marketPriceThreeMonthReturn,
        marketPriceOneYearReturn,
        marketPriceThreeYearReturn,
        marketPriceDailyYtdReturn,
        navOneMonthReturn,
        navThreeMonthReturn,
        navOneYearReturn,
        navThreeYearReturn,
        navDailyYtdReturn,
        benchmarkOneMonthReturn,
        benchmarkThreeMonthReturn,
        benchmarkOneYearReturn,
        benchmarkThreeYearAnnualReturn,
        benchmarkDailyYtdReturn
    ].map((value) => value >= 40);

    const hasExtraordinaryPerformance = listOfPerformanceChecks.includes(true);

    return {
        data: returnData,
        headers,
        primaryBenchmarkName: primaryBenchmark
            ? primaryBenchmark.benchmarkName
            : '',
        inceptionDate: displayShortDateOrNA(inceptionDate),
        asOfDate: displayShortDateOrNA(dateByPeriod),
        quarterlyEndDate,
        grossExpenseRatio: displayPercentOrNaTruthyZero(grossExpenseRatio),
        netExpenseRatio: displayPercentOrNaTruthyZero(netExpenseRatio),
        hasExtraordinaryPerformance
    };
};
